// (3) Practice topic picker. Topics are board+grade scoped server-side (JWT) —
// students never see another board/grade's topics.
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiGet } from '@/lib/api';
import { decodeClaims } from '@/lib/auth';
import type { BandLevel, PracticeTopic, SubjectDTO } from '@/types';
import { BAND_FILL } from '@/lib/bins';
import { frequencyTiers } from '@/lib/frequency';
import { FrequencyBadge } from './FrequencyBadge';

const BAND_RANK: Record<BandLevel, number> = { ORANGE_PINK: 0, BLUE: 1, GREEN: 2 };
const BAND_TAG: Record<BandLevel, string> = { GREEN: 'Mastered', BLUE: 'Growing', ORANGE_PINK: 'Needs work' };

export function TopicPicker({ onPick }: { onPick: (topic: PracticeTopic) => void }) {
  const [sort, setSort] = useState<'frequency' | 'band'>('frequency');
  const claims = decodeClaims();

  const { data, isLoading } = useQuery({
    queryKey: ['practice-topics'],
    queryFn: () => apiGet<PracticeTopic[]>('/practice/topics'),
  });

  // Same subjects query DashboardPage/HomePage already fetch — shares the
  // React Query cache, so this is free when navigating between them.
  const subjectsQuery = useQuery({
    queryKey: ['subjects', claims?.board, claims?.grade],
    enabled: Boolean(claims?.board && claims?.grade),
    queryFn: () => apiGet<SubjectDTO[]>(`/subjects?board=${claims!.board}&grade=${claims!.grade}`),
  });
  const subjectName = (id: string) => subjectsQuery.data?.find((s) => s.id === id)?.name ?? 'Topic';

  const topics = useMemo(() => {
    const list = [...(data ?? [])];
    if (sort === 'frequency') return list.sort((a, b) => b.frequencyCount - a.frequencyCount);
    // Worst band first; null bands sort last.
    return list.sort((a, b) => rank(a.confidenceBand) - rank(b.confidenceBand));
  }, [data, sort]);

  const tiers = useMemo(() => frequencyTiers(topics, (t) => t.frequencyCount), [topics]);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="h-28 animate-pulse rounded-2xl" style={{ background: 'var(--surface-sunk)' }} />
        ))}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-sm font-bold" style={{ color: 'var(--text-body)' }}>
          Pick a topic to practise
        </h2>
        <button
          onClick={() => setSort((s) => (s === 'frequency' ? 'band' : 'frequency'))}
          className="rounded-full px-3 py-1.5 text-xs font-bold"
          style={{ background: 'var(--paper-sunk)', color: 'var(--ink-2)' }}
        >
          Sort: {sort === 'frequency' ? 'By frequency' : 'Worst band first'}
        </button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {topics.map((t) => (
          <button
            key={t.topicId}
            onClick={() => onPick(t)}
            className="flex flex-col gap-2.5 rounded-2xl border p-4 text-left transition hover:border-forest"
            style={{ borderColor: 'var(--border-default)', background: 'var(--surface-card)' }}
          >
            <div className="flex items-center justify-between gap-2">
              <span
                className="rounded-full px-2.5 py-1 text-fs-11.5 font-bold"
                style={{ background: 'var(--paper-sunk)', color: 'var(--ink-2)' }}
              >
                {subjectName(t.subjectId)}
              </span>
              <FrequencyBadge tier={tiers.get(t) ?? 'Low'} />
            </div>
            <span className="text-fs-16 font-extrabold tracking-tight" style={{ color: 'var(--text-strong)' }}>
              {t.name}
            </span>
            {t.confidenceBand && (
              <span
                className="inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-1 text-fs-11 font-bold"
                style={{ background: BAND_FILL[t.confidenceBand], color: '#fff' }}
              >
                {BAND_TAG[t.confidenceBand]}
              </span>
            )}
          </button>
        ))}
        {topics.length === 0 && (
          <p className="col-span-full text-sm" style={{ color: 'var(--ink-3)' }}>
            No topics available yet.
          </p>
        )}
      </div>
    </div>
  );
}

function rank(band: BandLevel | null): number {
  return band === null ? 99 : BAND_RANK[band];
}
